/**
 * ============================================================================
 * ASTROVEDA KUNDALI - AUTOMATED VERSION & RELEASE MANAGEMENT
 * ============================================================================
 * Supports:
 * - Option A: Build-time Git SHA commit hash, branch, and build timestamp stamping
 * - Option B: Semantic versioning with release metadata
 * - Retention Policy: Strictly retains only the LAST 6 APP VERSIONS in history/snapshots
 */

// Global constants injected by Vite at build-time (vite.config.ts)
declare const __APP_VERSION__: string;
declare const __GIT_COMMIT__: string;
declare const __GIT_BRANCH__: string;
declare const __BUILD_TIME__: string;

export interface AppVersionInfo {
  version: string;
  gitCommit: string;
  gitBranch: string;
  buildTime: string;
  displayVersion: string;
  formattedBuildDate: string;
}

export interface AppReleaseRecord {
  version: string;
  releaseDate: string;
  title: string;
  titleHi: string;
  highlights: string[];
  highlightsHi: string[];
  type: 'major' | 'minor' | 'patch';
}

export interface AppVersionSnapshot {
  id: string;
  version: string;
  gitCommit: string;
  timestamp: string;
  notes?: string;
  profileCount?: number;
  environment: 'production' | 'preview' | 'development';
}

/**
 * RETENTION LIMIT: Only the last 10 versions/snapshots are retained in local storage & history.
 */
export const MAX_RETAINED_VERSIONS = 10;

/**
 * Official Release History (Capped to 10 versions)
 */
export const APP_RELEASE_HISTORY: AppReleaseRecord[] = [
  {
    version: '6.2.0',
    releaseDate: '2026-09-22',
    title: 'Bhavesha Panchadha Maitri & Dignity Inspector',
    titleHi: 'भावेश पंचधा मैत्री एवं गरिमा इंस्पेक्टर',
    type: 'minor',
    highlights: [
      'House Lord (Bhavesha) Panchadha Maitri compound relationship calculations with hover popovers',
      'Harmonized Dignity themes, Neecha Bhanga Rajayoga indicators, and Jaimini Chara Karakas',
      'Vedic planetary sequencing: Occupants & Residing Lords first, Aspecting Grahas systematically at end',
      'Automated Git SHA stamping and build timestamps'
    ],
    highlightsHi: [
      'भावेश पंचधा मैत्री संबंध एवं विस्तृत होवर पॉपओवर इंस्पेक्टर',
      'समान गरिमा थीम, नीच भंग राजयोग एवं जैमिनी कारक सूचक',
      'वैदिक ग्रहीय अनुक्रम: स्थित ग्रह व भावेश पहले, दृष्टि डालने वाले ग्रह अंत में',
      'स्वचालित गिट कमिट स्टाम्प'
    ]
  },
  {
    version: '6.1.0',
    releaseDate: '2026-09-18',
    title: 'Gochar Transit Timing & Kakshya Synergy',
    titleHi: 'गोचर पारगमन एवं कक्ष्य तालमेल',
    type: 'minor',
    highlights: [
      'Real-time Gochar transit duration calculations and upcoming zodiac ingress predictions',
      'Ashtakavarga BAV/SAV house climate verdict and transit synergy metrics',
      'Interactive House Inspector with dynamic transit countdown badges'
    ],
    highlightsHi: [
      'गोचर पारगमन अवधि गणना एवं आगामी राशि प्रवेश भविष्यवाणी',
      'अष्टकवर्ग रेखा/बिंदु तालमेल एवं भाव जलवायु विश्लेषण',
      'इंटरैक्टिव भाव इंस्पेक्टर व पारगमन टाइमर'
    ]
  },
  {
    version: '6.0.0',
    releaseDate: '2026-09-10',
    title: 'Vedic Analysis Engine & Shadbala Matrix',
    titleHi: 'वैदिक विश्लेषण इंजन एवं षड्बल मैट्रिक्स',
    type: 'major',
    highlights: [
      'Comprehensive 6-fold Shadbala calculation (Sthana, Dig, Kala, Chesta, Naisargika, Drik)',
      'Bhava Sandhi and Chalit chart adjustments with high-precision orb detections',
      'Harmonized bilingual Vedic terminology across Hindi and English'
    ],
    highlightsHi: [
      'सम्पूर्ण 6-गुना षड्बल विश्लेषण (स्थान, दिग्, काल, चेष्टा, नैसर्गिक, दृग बल)',
      'भाव संधि एवं चलित चक्र समायोजन',
      'हिंदी एवं अंग्रेजी में परिष्कृत वैदिक शब्दावली'
    ]
  },
  {
    version: '5.9.0',
    releaseDate: '2026-09-01',
    title: 'Muhurta Advisor & Daily Panchang Planner',
    titleHi: 'मुहूर्त सलाहकार एवं दैनिक पंचांग',
    type: 'minor',
    highlights: [
      'Solar sunrise/sunset accurate Choghadiya and Shubh Hora calculation',
      'Rahu Kalam, Yamaganda, and Gulika Kalam avoidance windows',
      'Custom date picker for auspicious event planning'
    ],
    highlightsHi: [
      'सूर्योदय/सूर्यास्त आधारित चौघड़िया एवं शुभ होरा गणना',
      'राहुकाल, यमगंड व गुलिक काल निषेध समय',
      'शुभ मुहूर्त चयन हेतु तिथि चयनकर्ता'
    ]
  },
  {
    version: '5.8.0',
    releaseDate: '2026-08-20',
    title: 'RBAC Access Control & Cloud Synchronization',
    titleHi: 'भूमिका-आधारित नियंत्रण एवं क्लाउड सिंक',
    type: 'minor',
    highlights: [
      'Multi-role RBAC framework (Super Admin, Astrologer, Basic, Guest)',
      'Firebase Firestore persistent chart storage with deduplication',
      'Encrypted client backup and restore vault'
    ],
    highlightsHi: [
      'बहु-भूमिका आरबीएसी ढांचा (सुपर एडमिन, ज्योतिषी, सामान्य उपयोगकर्ता)',
      'फायरबेस क्लाउड डेटाबेस में सुरक्षित कुंडली संग्रहण',
      'एन्क्रिप्टेड बैकअप एवं पुनर्प्राप्ति'
    ]
  },
  {
    version: '5.7.0',
    releaseDate: '2026-08-05',
    title: 'Core Astronomical Engine & D1-D60 Vargas',
    titleHi: 'मूल खगोलीय इंजन एवं D1-D60 वर्ग चक्र',
    type: 'major',
    highlights: [
      'High-precision planetary ephemeris and Lahiri Ayanamsha calculations',
      'Divisional charts (D1 Rasi to D60 Shashtiamsha) with North/South/East layouts',
      '5-level Vimshottari Dasha timeline (Maha, Antar, Pratyantar, Sookshma, Prana)'
    ],
    highlightsHi: [
      'लाहिड़ी अयनांश आधारित उच्च परिशुद्धता खगोलीय गणना',
      'वर्ग चक्र D1 से D60 (उत्तर/दक्षिण/पूर्व शैलियों में)',
      'विंशोत्तरी दशा 5-स्तरीय समयावली'
    ]
  }
];

/**
 * Returns current build and runtime version information
 */
export function getAppVersionInfo(): AppVersionInfo {
  let version = '6.2.0';
  let gitCommit = 'dev';
  let gitBranch = 'main';
  let buildTime = new Date().toISOString();

  try {
    if (typeof __APP_VERSION__ !== 'undefined') version = __APP_VERSION__;
    if (typeof __GIT_COMMIT__ !== 'undefined') gitCommit = __GIT_COMMIT__;
    if (typeof __GIT_BRANCH__ !== 'undefined') gitBranch = __GIT_BRANCH__;
    if (typeof __BUILD_TIME__ !== 'undefined') buildTime = __BUILD_TIME__;
  } catch {}

  const buildDateObj = new Date(buildTime);
  const formattedBuildDate = isNaN(buildDateObj.getTime())
    ? buildTime
    : buildDateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });

  const displayVersion = `v${version}${gitCommit && gitCommit !== 'dev' ? ` (${gitCommit})` : ''}`;

  return {
    version,
    gitCommit,
    gitBranch,
    buildTime,
    displayVersion,
    formattedBuildDate
  };
}

const LOCAL_STORAGE_SNAPSHOT_KEY = 'astro_app_version_snapshots';

/**
 * Retrieves stored application version snapshots, capped to MAX_RETAINED_VERSIONS (10)
 */
export function getStoredVersionSnapshots(): AppVersionSnapshot[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_SNAPSHOT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.slice(0, MAX_RETAINED_VERSIONS);
    }
  } catch (err) {
    console.warn('Failed to parse version snapshots:', err);
  }
  return [];
}

/**
 * Records a new version snapshot and automatically purges old snapshots beyond 10 versions to save space.
 */
export function recordVersionSnapshot(notes?: string, profileCount?: number): AppVersionSnapshot[] {
  const currentInfo = getAppVersionInfo();
  const existing = getStoredVersionSnapshots();

  const newSnapshot: AppVersionSnapshot = {
    id: `snap_${Date.now()}_${currentInfo.gitCommit}`,
    version: currentInfo.version,
    gitCommit: currentInfo.gitCommit,
    timestamp: new Date().toISOString(),
    notes: notes || `Build snapshot ${currentInfo.displayVersion}`,
    profileCount: profileCount ?? 0,
    environment: window.location.hostname.includes('ais-dev') ? 'development' : 'production'
  };

  // Prepend new snapshot, remove duplicates, and enforce MAX 10 version limit to save space
  const updated = [
    newSnapshot,
    ...existing.filter(s => !(s.version === newSnapshot.version && s.gitCommit === newSnapshot.gitCommit))
  ].slice(0, MAX_RETAINED_VERSIONS);

  try {
    localStorage.setItem(LOCAL_STORAGE_SNAPSHOT_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Failed to save version snapshot:', err);
  }

  return updated;
}
